import {
  AssetType,
  TransformationVariant,
  DeviceFamily,
  Placement,
} from '@amzn/fire-tv-campaign-automation-service';
import { KatDropdown, KatFileUpload, KatInput } from '@amzn/katal-react';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getUserAlias } from 'src/components/authentication/AuthenticationUtils';
import { AssetInputProps } from 'src/components/pages/campaignAutomation/input/assets';
import { getVariantsFromConfig } from 'src/config/templateVariantConfiguration';
import { getDefaultInputType } from 'src/configs/assetConfig';
import {
  BASE_CAMPAIGN_METADATA_CONFIG,
  TextMetadataConfig,
} from 'src/configs/campaignMetadataConfig';
import { getTransformationTriggerFields } from 'src/configs/logoTextInputConfig';
import {
  CONFIRMATION_CONTEXT,
  StateConstants,
  TranslatableConstants,
  AssetConstants,
  ProvisionedConstants,
  STAGE,
  TEXT_BASED_TITLE_DESCRIPTION_LENGTH,
} from 'src/constants';
import { useAuthentication } from 'src/contexts/AuthenticationContext';
import { useSelection } from 'src/contexts/SelectionContext';
import { exhaustiveCheck } from 'src/helpers';
import { logger } from 'src/logger';
import { AssetStatus, InputType } from 'src/types';
import { validateUrl } from 'src/utils/assetInput/assetInputUtils';
import { timeToSeconds } from 'src/utils/DateTimeUtils';

import { useAssetContext } from '../useAssetContext';
import { useCampaignMetadata } from '../useCampaignMetadata';

import { useOverlayTransform } from './useOverlayTransform';

/**
 * Return type for the useAssetInput hook
 */
interface UseAssetInputReturn {
  /** Current selected input type (URL or File Upload) */
  selectedInputType: InputType;

  /** Current URL input value */
  url: string;

  /** Whether to show the confirmation dialog */
  showConfirmDialog: boolean;

  /** Generated unique name for the radio group */
  radioGroupName: string;

  /** Whether the current URL input is valid and not previously fetched */
  isValidUrlInput: boolean;

  /** Whether there is an existing asset (completed or pending upload) */
  hasExistingAsset: boolean;

  /** Selected image title from search */
  selectedImageTitle: string | undefined;

  /** Device family from selection context */
  deviceFamily: DeviceFamily;

  /** Placement from selection context */
  placement: Placement;

  /**
   * Handles URL input changes
   * @param event - Katal input change event
   */
  handleUrlChange: (event: KatInput.ChangeEvent) => void;

  /**
   * Handles catalog selection (search results)
   * @param imageUrl - Selected image URL
   * @param imageTitle - Selected image title
   */
  handleCatalogSelection: (imageUrl: string, imageTitle?: string) => void;

  /**
   * Handles URL fetch action
   */

  handleUrlFetch: () => void;

  /**
   * Handles URL removal action
   * Shows confirmation dialog if there's an existing asset
   */
  handleUrlRemoval: () => void;

  /**
   * Processes URL removal after confirmation
   * Clears the URL and resets asset state
   */
  processUrlRemoval: () => void;

  /**
   * Handles file attachment events
   * @param event - Katal file attached event
   */
  handleFileAttached: (event: KatFileUpload.FilesAttachedEvent) => void;

  /**
   * Handle file removed events
   */
  handleFileRemoved: () => void;

  /**
   * Handles file replacement events
   * @param event - Katal file replaced event
   */
  handleFileReplaced: (event: KatFileUpload.FilesReplacedEvent) => void;

  /**
   * Handles file attachment error events
   * @param event - Katal file attachment error event
   */
  handleFileAttachedError: (event: KatFileUpload.FilesAttachedErrorEvent) => void;

  /**
   * Handles input type radio button changes
   * Shows confirmation dialog if there's an existing asset
   * @param event - Material UI radio change event
   */
  handleRadioChange: (event: ChangeEvent<HTMLInputElement>) => void;

  /**
   * Confirms the pending input type or URL change
   */
  handleConfirmTypeChange: () => void;

  /**
   * Cancels the pending input type or URL change
   */
  handleCancelTypeChange: () => void;

  /**
   * Handle change the TransformationVariant
   */
  handleTransformationVariantChange: (event: KatDropdown.ChangeEvent) => void;

  /**
   * Clears selected image from search
   */
  handleClearSelectedImage: () => void;

  /**
   * Gets the appropriate confirmation message based on the current context
   * @returns Message to display in the confirmation dialog
   */
  getConfirmationMessage: () => string;
}

interface AutoPopulateFieldConfig {
  id: string;
  sourceField: string;
  maxLength: number;
  getValue: () => string;
  setValue: (value: string) => void;
}

/**
 * Hook for managing asset input state and logic.
 * Handles both file uploads and URL inputs with confirmation dialogs
 * for replacing existing assets.
 *
 * @param props - Asset input configuration
 * @param props.label - Label for the input (used for generating unique IDs)
 * @param props.assetInputHandler - Handler for asset-related actions
 * @param props.status - Current status of the asset
 *
 * @example
 * // Basic usage
 * const {
 *   selectedType,
 *   url,
 *   handleUrlChange,
 *   handleUrlFetch,
 *   handleFileAttached
 * } = useAssetInput({
 *   label: 'Background Image',
 *   assetInputHandler: backgroundImageHandler,
 *   status: AssetStatus.PENDING_INPUT
 * });
 *
 * @example
 * // Handling URL input
 * const {
 *   url,
 *   isValidUrlInput,
 *   handleUrlChange,
 *   handleUrlFetch
 * } = useAssetInput(props);
 *
 * return (
 *   <UrlInput
 *     url={url}
 *     isValidUrl={isValidUrlInput}
 *     onChange={handleUrlChange}
 *     onFetch={handleUrlFetch}
 *   />
 * );
 */
export const useAssetInput = ({
  label,
  assetInputHandler,
  status,
  assetDetails,
  assetValidationHandler,
  assetTransformationHandler,
  setState,
  assetType,
}: Pick<
  AssetInputProps,
  | 'label'
  | 'assetInputHandler'
  | 'status'
  | 'assetValidationHandler'
  | 'assetDetails'
  | 'setState'
  | 'assetType'
  | 'assetTransformationHandler'
>): UseAssetInputReturn => {
  const { assetStates, updateAssetState } = useAssetContext();
  const { placement, deviceFamily, locale } = useSelection();
  const { metadata, updateMetadata } = useCampaignMetadata();
  const { user } = useAuthentication();
  const userAlias = useMemo(() => getUserAlias(user), [user]);
  const assetState = assetStates[assetType];

  // State
  const [selectedInputType, setSelectedInputType] = useState<Record<AssetType, InputType>>(() => ({
    [AssetType.BACKGROUND_IMAGE]:
      getDefaultInputType(AssetType.BACKGROUND_IMAGE, deviceFamily, placement) || InputType.SEARCH,
    [AssetType.LOGO]:
      getDefaultInputType(AssetType.LOGO, deviceFamily, placement) || InputType.LOGO_TEXT,
    [AssetType.VIDEO_TRAILER]:
      getDefaultInputType(AssetType.VIDEO_TRAILER, deviceFamily, placement) || InputType.UPLOAD,
  }));

  const updateSelectedInputType = (inputType: InputType) => {
    setSelectedInputType((prev) => ({
      ...prev,
      [assetType]: inputType,
    }));
  };
  const [url, setUrl] = useState<string>(StateConstants.EMPTY_STRING);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [pendingUrl, setPendingUrl] = useState<string>(StateConstants.EMPTY_STRING);
  const [pendingImageTitle, setPendingImageTitle] = useState<string | undefined>(undefined);
  const [confirmationContext, setConfirmationContext] = useState<
    (typeof CONFIRMATION_CONTEXT)[keyof typeof CONFIRMATION_CONTEXT]
  >(CONFIRMATION_CONTEXT.INPUT_TYPE);
  const [selectedImageTitle, setSelectedImageTitle] = useState<string | undefined>(undefined);
  const isMounted = useRef(false);
  const pendingRadioChangeRef = useRef<InputType | null>(null);
  const prevAutoPopulatedRef = useRef<Record<string, string>>({});
  const assetStatesRef = useRef(assetStates);
  assetStatesRef.current = assetStates;
  const metadataRef = useRef(metadata);
  metadataRef.current = metadata;

  useEffect(() => {
    if (assetType !== AssetType.LOGO) return;

    const variant = assetState.transformationVariant;

    // Skip transformation trigger for NO_TRANSFORMATION variant
    if (variant === TransformationVariant.NO_TRANSFORMATION) {
      return;
    }
    const transformationTriggerFields = getTransformationTriggerFields(variant);
    const hasRequiredFields = transformationTriggerFields.every((field) =>
      Boolean(assetState[field]),
    );

    if (hasRequiredFields) {
      setState({ status: AssetStatus.PENDING_TRANSFORMATION });
    }
  }, [
    assetType,
    assetState.cta,
    assetState.logoTitle,
    assetState.logoDescription,
    assetState.providerLogo,
    assetState.transformationVariant,
  ]);

  // Update selected input type when device family or placement changes
  useEffect(() => {
    const newDefaultInputType = getDefaultInputType(assetType, deviceFamily, placement);
    if (newDefaultInputType) {
      updateSelectedInputType(newDefaultInputType);
    }

    // Reset transformation variant to first available option
    const variants = getVariantsFromConfig(
      ProvisionedConstants.STAGE as STAGE,
      deviceFamily,
      placement,
    )?.[assetType];
    if (variants && variants.length > 0) {
      setState({ transformationVariant: variants[0].value as TransformationVariant });
    }
  }, [deviceFamily, placement, assetType]);

  // Clear local state when asset state is reset (e.g., by locale change)
  useEffect(() => {
    if (status === AssetStatus.PENDING_INPUT && !assetState.key) {
      setUrl(StateConstants.EMPTY_STRING);
      setSelectedImageTitle(undefined);
    }
  }, [status, assetState.key]);

  useEffect(() => {
    isMounted.current = true;

    switch (status) {
      case AssetStatus.PENDING_VALIDATION:
        if (isMounted.current) assetValidationHandler?.validateAsset(assetDetails);
        break;

      case AssetStatus.PENDING_TRANSFORMATION:
        if (isMounted.current)
          assetTransformationHandler.handleTransformation({
            assetType,
            placement,
            actor: userAlias,
            provider: metadata.provider,
            sourceAssetKey: assetDetails.key,
            sourceAssetKeys: assetState.sourceAssetKeys?.length
              ? assetState.sourceAssetKeys
              : undefined,
            cta: assetState.cta,
            logoTitle: assetState.logoTitle,
            logoDescription: assetState.logoDescription,
            providerLogo: assetState.providerLogo,
            transformationVariant: assetState.transformationVariant,
            startTime: assetState.startTime
              ? timeToSeconds(assetState.startTime)
              : AssetConstants.VIDEO_DURATION.DEFAULT_START_TIME,
            endTime: assetState.endTime
              ? timeToSeconds(assetState.endTime)
              : AssetConstants.VIDEO_DURATION.DEFAULT_END_TIME,
            topOffset: assetState.topOffset,
            leftOffset: assetState.leftOffset,
            contentBadgeText: assetState.overlayToggles?.contentBadge
              ? assetState.contentBadge
              : undefined,
            disclaimerText: assetState.overlayToggles?.disclaimer
              ? assetState.disclaimer
              : undefined,
            contentRating: assetState.overlayToggles?.contentRating
              ? assetState.contentRating
              : undefined,
            mediaText: assetState.mediaText,
            locale,
            genres: assetState.genres,
          });
        break;

      default:
        break;
    }

    return () => {
      isMounted.current = false;
    };
  }, [
    status,
    assetDetails,
    assetValidationHandler,
    assetTransformationHandler,
    userAlias,
    assetState,
    placement,
    metadata.provider,
  ]);

  // Overlay transform lifecycle (debounce, cache, revert on failure)
  useOverlayTransform({
    assetType,
    status,
    assetState,
    setState,
    assetTransformationHandler,
    placement,
    userAlias,
  });

  // Generate unique radio group name from label
  const radioGroupName = useMemo(
    () => `asset-input__${label.toLowerCase().replace(/\s+/g, '-')}`,
    [label],
  );

  // State to track the last fetched URL
  const [lastFetchedUrlState, setLastFetchedUrlState] = useState<string>(
    StateConstants.EMPTY_STRING,
  );

  // Check if URL is valid and not previously fetched
  const isValidUrlInput = useMemo(
    () => validateUrl(url) && url !== lastFetchedUrlState,
    [url, lastFetchedUrlState],
  );

  // Check if there's an existing asset
  const hasExistingAsset = useMemo(
    () =>
      Boolean(
        status &&
          [AssetStatus.COMPLETED, AssetStatus.PENDING_UPLOAD, AssetStatus.FAILED].includes(status),
      ),
    [status],
  );

  /**
   * Clears all input state and errors
   * This is a core function used by other handlers to reset the input state
   */
  const clearInputState = useCallback(() => {
    setUrl(StateConstants.EMPTY_STRING);
    setLastFetchedUrlState(StateConstants.EMPTY_STRING);
    assetInputHandler.clearError();
    assetInputHandler.resetAssetState();
    prevAutoPopulatedRef.current = {};
  }, [assetInputHandler]);

  /**
   * Updates the input type and clears existing state
   * @param newType - The new input type to set
   */
  const updateInputType = useCallback(
    (newType: InputType) => {
      clearInputState();
      switch (newType) {
        case InputType.LOGO_TEXT:
          // Get device-family-specific variant instead of hardcoding
          const variants = getVariantsFromConfig(
            ProvisionedConstants.STAGE as STAGE,
            deviceFamily,
            placement,
          )?.[assetType];
          const logoVariant = variants?.[0]?.value as TransformationVariant;
          setState({
            transformationVariant:
              logoVariant || TransformationVariant.TEXT_BASED_TITLE_DESCRIPTION,
          });
          break;

        default:
          if (assetType === AssetType.LOGO)
            setState({ transformationVariant: TransformationVariant.NO_TRANSFORMATION });
          break;
      }
      updateSelectedInputType(newType);
      updateAssetState(assetType, { inputType: newType });
    },
    [clearInputState, setState, assetType, updateAssetState, deviceFamily, placement],
  );

  /**
   * Returns config for fields that should be auto-populated from search selection.
   * Each field specifies a max length to truncate titles that exceed limits.
   */
  const getAutoPopulateFields = useCallback(
    (): AutoPopulateFieldConfig[] => [
      {
        id: 'logoTitle',
        sourceField: 'title',
        maxLength: TEXT_BASED_TITLE_DESCRIPTION_LENGTH.LOGO_TITLE_MAX_LENGTH_WITHOUT_DESCRIPTION,
        getValue: () => assetStatesRef.current[AssetType.LOGO]?.logoTitle || '',
        setValue: (v: string) => updateAssetState(AssetType.LOGO, { logoTitle: v }),
      },
      {
        id: 'campaignTitle',
        sourceField: 'title',
        maxLength: (BASE_CAMPAIGN_METADATA_CONFIG.title as TextMetadataConfig).maxLength!,
        getValue: () => metadataRef.current.title || '',
        setValue: (v: string) => updateMetadata({ title: v }),
      },
      {
        id: 'accessibilityText',
        sourceField: 'title',
        maxLength: (BASE_CAMPAIGN_METADATA_CONFIG.accessibilityText as TextMetadataConfig)
          .maxLength!,
        getValue: () => metadataRef.current.accessibilityText || '',
        setValue: (v: string) => updateMetadata({ accessibilityText: v }),
      },
    ],
    [updateAssetState, updateMetadata],
  );

  const autoPopulateFromSearch = useCallback(
    (searchData: Record<string, string>) => {
      const fields = getAutoPopulateFields();
      fields.forEach((field) => {
        const newValue = searchData[field.sourceField];
        if (!newValue) return;
        const key = field.id;
        const currentValue = field.getValue();
        const wasAutoPopulated = key in prevAutoPopulatedRef.current;
        const isUnchangedFromAutoPopulate = currentValue === prevAutoPopulatedRef.current[key];
        if (!currentValue || (wasAutoPopulated && isUnchangedFromAutoPopulate)) {
          const truncated = newValue.slice(0, field.maxLength);
          field.setValue(truncated);
          prevAutoPopulatedRef.current[key] = truncated;
        }
      });
    },
    [getAutoPopulateFields],
  );

  /**
   * Clears auto-populated fields that the user hasn't manually edited.
   */
  const clearAutoPopulatedFields = useCallback(() => {
    const fields = getAutoPopulateFields();
    fields.forEach((field) => {
      const key = field.id;
      if (
        key in prevAutoPopulatedRef.current &&
        field.getValue() === prevAutoPopulatedRef.current[key]
      ) {
        field.setValue('');
      }
    });
    prevAutoPopulatedRef.current = {};
  }, [getAutoPopulateFields]);

  // Auto-populate fields only after background image reaches COMPLETED status
  const pendingAutoPopulateTitleRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (
      assetType === AssetType.BACKGROUND_IMAGE &&
      status === AssetStatus.COMPLETED &&
      pendingAutoPopulateTitleRef.current
    ) {
      autoPopulateFromSearch({ title: pendingAutoPopulateTitleRef.current });
      pendingAutoPopulateTitleRef.current = undefined;
    }
  }, [assetType, status, autoPopulateFromSearch]);

  /**
   * Processes a URL fetch operation
   * Handles the URL through the asset handler and updates state
   * @param urlToFetch - The URL to process
   * @param imageTitle - Optional title for search images
   */
  const processUrlFetch = useCallback(
    (urlToFetch: string, imageTitle?: string) => {
      assetInputHandler.handleUrl(urlToFetch);
      setLastFetchedUrlState(urlToFetch);
      setUrl(urlToFetch);
      if (imageTitle) {
        setSelectedImageTitle(imageTitle);
        updateAssetState(assetType, { inputType: InputType.SEARCH });
        if (assetType === AssetType.BACKGROUND_IMAGE) {
          pendingAutoPopulateTitleRef.current = imageTitle;
        }
      } else {
        updateAssetState(assetType, { inputType: InputType.URL });
      }
    },
    [assetInputHandler, updateAssetState, assetType],
  );

  /**
   * Handles URL input changes
   * @param event - Katal input change event
   */
  const handleUrlChange = useCallback((event: KatInput.ChangeEvent) => {
    setUrl(event.currentTarget.value);
  }, []);

  /**
   * Handles URL fetch action for manual URL input
   */
  const handleUrlFetch = useCallback(() => {
    if (isValidUrlInput) {
      if (hasExistingAsset) {
        setPendingUrl(url);
        setConfirmationContext(CONFIRMATION_CONTEXT.URL);
        setShowConfirmDialog(true);
      } else {
        processUrlFetch(url);
      }
    }
  }, [url, isValidUrlInput, hasExistingAsset, processUrlFetch]);

  /**
   * Handles catalog selection (search results)
   * @param imageUrl - Selected image URL
   * @param imageTitle - Selected image title
   */
  const handleCatalogSelection = useCallback(
    (imageUrl: string, imageTitle?: string) => {
      if (validateUrl(imageUrl)) {
        if (hasExistingAsset) {
          setPendingUrl(imageUrl);
          setPendingImageTitle(imageTitle);
          setConfirmationContext(CONFIRMATION_CONTEXT.CATALOG_SELECTION);
          setShowConfirmDialog(true);
        } else {
          processUrlFetch(imageUrl, imageTitle);
        }
      }
    },
    [hasExistingAsset, processUrlFetch],
  );

  /**
   * Common function to clear URL and reset validation state
   */
  const clearUrlAndValidation = useCallback(() => {
    setUrl(StateConstants.EMPTY_STRING);
    assetInputHandler.resetAssetState();
    setLastFetchedUrlState(StateConstants.EMPTY_STRING);
    setSelectedImageTitle(undefined);
    if (assetType === AssetType.BACKGROUND_IMAGE) {
      clearAutoPopulatedFields();
      pendingAutoPopulateTitleRef.current = undefined;
    }
  }, [assetInputHandler, assetType, clearAutoPopulatedFields]);

  const processUrlRemoval = useCallback(() => {
    clearUrlAndValidation();
    setShowConfirmDialog(false);
  }, [assetInputHandler, clearUrlAndValidation]);

  /**
   * Handles URL removal action
   * Shows confirmation dialog if there's an existing asset
   */
  const handleUrlRemoval = useCallback(() => {
    if (hasExistingAsset) {
      setConfirmationContext(CONFIRMATION_CONTEXT.URL_CLEAR);
      setShowConfirmDialog(true);
    } else {
      clearUrlAndValidation();
    }
  }, [hasExistingAsset, clearUrlAndValidation]);

  /**
   * Handles file attachment events
   * @param event - Katal file attached event
   */
  const handleFileAttached = useCallback(
    (event: KatFileUpload.FilesAttachedEvent) => {
      assetInputHandler.handleFileAttached(event);
      updateAssetState(assetType, { inputType: InputType.UPLOAD });
    },
    [assetInputHandler, updateAssetState, assetType],
  );

  const handleFileRemoved = useCallback(() => {
    assetInputHandler.resetAssetState();
  }, [assetInputHandler]);

  /**
   * Handles file replacement events
   * @param event - Katal file replaced event
   */
  const handleFileReplaced = useCallback(
    (event: KatFileUpload.FilesReplacedEvent) => {
      assetInputHandler.handleFileReplaced(event);
      updateAssetState(assetType, { inputType: InputType.UPLOAD });
    },
    [assetInputHandler, updateAssetState, assetType],
  );

  /**
   * Handles file attachment error events
   * Sets validation error flag and delegates to handler
   * @param event - Katal file attachment error event
   */
  const handleFileAttachedError = useCallback(
    (event: KatFileUpload.FilesAttachedErrorEvent) => {
      assetInputHandler.handleFileAttachedError(event);
    },
    [assetInputHandler],
  );

  /**
   * Handles input type radio button changes
   * Shows confirmation dialog if there's an existing asset
   * @param event - Material UI radio change event
   */
  const handleRadioChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      if (value && Object.values(InputType).includes(value as InputType)) {
        const newInputType = value as InputType;

        if (hasExistingAsset) {
          pendingRadioChangeRef.current = newInputType;
          setConfirmationContext(CONFIRMATION_CONTEXT.INPUT_TYPE);
          setShowConfirmDialog(true);
        } else {
          updateInputType(newInputType);
        }
      }
    },
    [hasExistingAsset, updateInputType],
  );

  /**
   * Confirms the pending input type or URL change
   * Handles both input type and URL confirmation contexts
   */
  const handleConfirmTypeChange = useCallback(() => {
    switch (confirmationContext) {
      case CONFIRMATION_CONTEXT.INPUT_TYPE:
        if (pendingRadioChangeRef.current) {
          updateInputType(pendingRadioChangeRef.current);
          pendingRadioChangeRef.current = null;
          setShowConfirmDialog(false);
        }
        break;

      case CONFIRMATION_CONTEXT.URL:
        if (pendingUrl) {
          processUrlFetch(pendingUrl);
          setPendingUrl(StateConstants.EMPTY_STRING);
          setShowConfirmDialog(false);
        }
        break;

      case CONFIRMATION_CONTEXT.CATALOG_SELECTION:
        if (pendingUrl) {
          processUrlFetch(pendingUrl, pendingImageTitle);
          setPendingUrl(StateConstants.EMPTY_STRING);
          setPendingImageTitle(undefined);
          setShowConfirmDialog(false);
        }
        break;

      case CONFIRMATION_CONTEXT.URL_CLEAR:
        processUrlRemoval();
        break;

      case CONFIRMATION_CONTEXT.CATALOG_SELECTION_CLEAR:
        processUrlRemoval();
        break;

      default:
        return exhaustiveCheck(confirmationContext);
    }
  }, [pendingUrl, processUrlFetch, confirmationContext, processUrlRemoval]);

  /**
   * Cancels the pending input type or URL change
   * Resets all pending state
   */
  const handleCancelTypeChange = useCallback(() => {
    setShowConfirmDialog(false);
    pendingRadioChangeRef.current = null;
    setPendingUrl(StateConstants.EMPTY_STRING);
    setPendingImageTitle(undefined);
  }, []);

  const handleTransformationVariantChange = useCallback(
    (event: KatDropdown.ChangeEvent) => {
      assetInputHandler.onTransformationVariantChange(event, assetDetails, status);
    },
    [assetInputHandler, assetDetails, status],
  );

  const handleClearSelectedImage = useCallback(() => {
    if (hasExistingAsset) {
      setConfirmationContext(CONFIRMATION_CONTEXT.CATALOG_SELECTION_CLEAR);
      setShowConfirmDialog(true);
    } else {
      setSelectedImageTitle(undefined);
      clearUrlAndValidation();
    }
  }, [hasExistingAsset, clearUrlAndValidation]);

  /**
   * Gets the appropriate confirmation message based on context
   * @returns Message to display in the confirmation dialog
   */
  const getConfirmationMessage = useCallback(() => {
    switch (confirmationContext) {
      case CONFIRMATION_CONTEXT.INPUT_TYPE:
        return TranslatableConstants.INPUT_TYPE_CONFIRMATION_MESSAGE;
      case CONFIRMATION_CONTEXT.URL:
        return TranslatableConstants.URL_CONFIRMATION_MESSAGE;
      case CONFIRMATION_CONTEXT.URL_CLEAR:
        return TranslatableConstants.URL_CLEAR_CONFIRMATION_MESSAGE;
      case CONFIRMATION_CONTEXT.CATALOG_SELECTION:
        return TranslatableConstants.CATALOG_SELECTION_CONFIRMATION_MESSAGE;
      case CONFIRMATION_CONTEXT.CATALOG_SELECTION_CLEAR:
        return TranslatableConstants.CATALOG_SELECTION_CLEAR_CONFIRMATION_MESSAGE;
      default:
        return exhaustiveCheck(confirmationContext);
    }
  }, [confirmationContext]);

  // Log status changes for debugging
  useEffect(() => {
    logger.debug(`AssetStatus changed to ${status}`);
  }, [status]);

  return {
    // State
    selectedInputType: selectedInputType[assetType],
    url,
    showConfirmDialog,

    // Computed values
    radioGroupName,
    isValidUrlInput,
    hasExistingAsset,
    selectedImageTitle,
    deviceFamily,
    placement,

    // Handlers
    handleUrlChange,
    handleUrlFetch,
    handleCatalogSelection,
    handleUrlRemoval,
    processUrlRemoval,
    handleFileAttached,
    handleFileRemoved,
    handleFileReplaced,
    handleFileAttachedError,
    handleRadioChange,
    handleConfirmTypeChange,
    handleCancelTypeChange,
    handleTransformationVariantChange,
    handleClearSelectedImage,
    getConfirmationMessage,
  };
};
